import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { motion } from 'framer-motion';
import { Building2, Calendar, ArrowUpCircle, Wallet, Trash2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { UpgradeRequest } from '../../types/upgrade';
import { useUpgradeStore } from '../../stores/upgradeStore';
import toast from 'react-hot-toast';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  requests: UpgradeRequest[];
};

type RequestGroup = {
  key: string;
  hotel: string;
  period: string;
  startDate?: string;
  endDate?: string;
  requests: UpgradeRequest[];
  totalRequests: number;
  totalAmount: number;
};

export const BatchOrganizerApprovalModal = ({ isOpen, onClose, requests }: Props) => {
  const { translations } = useLanguage();
  const [requestGroups, setRequestGroups] = useState<RequestGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isGrouping, setIsGrouping] = useState(false);
  const { submitPayment } = useUpgradeStore();

  // Group and analyze requests
  useEffect(() => {
    if (isOpen) {
      setIsGrouping(true);
      
      // Group by hotel and date period
      const groups: Record<string, RequestGroup> = {};
      
      requests.forEach(request => {
        if (!request.fromHotel) return;
        
        const period = request.startDate && request.endDate 
          ? `${request.startDate} - ${request.endDate}` 
          : 'No period';
        
        const key = `${request.fromHotel}|${period}`;
        
        if (!groups[key]) {
          groups[key] = {
            key,
            hotel: request.fromHotel,
            period,
            startDate: request.startDate,
            endDate: request.endDate,
            requests: [],
            totalRequests: 0,
            totalAmount: 0
          };
        }
        
        groups[key].requests.push(request);
        groups[key].totalRequests++;
        groups[key].totalAmount += (request.price || 0);
      });
      
      // Convert groups to array and sort by hotel and period
      const groupArray = Object.values(groups).sort((a, b) => {
        if (a.hotel !== b.hotel) return a.hotel.localeCompare(b.hotel);
        return a.period.localeCompare(b.period);
      });
      
      setRequestGroups(groupArray);
      setIsGrouping(false);
    }
  }, [isOpen, requests]);

  // Process batch approval
  const handleGroupApproval = async (group: RequestGroup) => {
    setIsProcessing(true);
    try {
      const confirmText = `Are you sure you want to deduct ${group.totalAmount} SAR from your balance for ${group.totalRequests} upgrades at ${group.hotel}?`;
      if (!window.confirm(confirmText)) {
        setIsProcessing(false);
        return;
      }
      
      // Process all requests in this group
      for (const request of group.requests) {
        await submitPayment(request.id);
      }
      
      toast.success(`Successfully processed ${group.totalRequests} upgrades for ${group.totalAmount} SAR`);
      
      // Update the current group in the state
      setRequestGroups(prev => 
        prev.filter(g => g.key !== group.key)
      );
      
      // Clear selected group
      setSelectedGroup(null);
      
    } catch (error) {
      console.error('Error processing batch deductions:', error);
      toast.error('Failed to process balance deductions');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Reject all requests in a group
  const handleRejectGroup = async (group: RequestGroup) => {
    const confirmReject = window.confirm(
      `Are you sure you want to reject all ${group.totalRequests} upgrade requests for ${group.hotel}?`
    );
    
    if (!confirmReject) return;
    
    setIsProcessing(true);
    try {
      for (const request of group.requests) {
        await useUpgradeStore.getState().rejectPayment(request.id);
      }
      
      toast.success(`Successfully rejected all ${group.totalRequests} requests`);
      
      // Update the current group in the state
      setRequestGroups(prev => 
        prev.filter(g => g.key !== group.key)
      );
      
    } catch (error) {
      console.error('Error rejecting group:', error);
      toast.error('Failed to reject requests');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={translations?.batchOrganizerApproval?.title || "Batch Balance Deduction"}
      width="90vw"
      maxWidth="800px"
    >
      <div className="space-y-6">
        {isGrouping ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            <span className="ml-3 text-gray-600">Analyzing upgrade requests...</span>
          </div>
        ) : requestGroups.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No pending upgrade requests found for balance deduction
          </div>
        ) : (
          <>
            {/* Introduction */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700">
                The following upgrades can be processed with a single balance deduction. Select a group to proceed.
              </p>
            </div>
            
            {/* Request Group List */}
            <div className="grid gap-4 grid-cols-1">
              {requestGroups.map(group => (
                <motion.div 
                  key={group.key}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`border rounded-lg overflow-hidden transition-colors ${
                    selectedGroup === group.key
                      ? 'border-red-500 shadow-md'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gray-50">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <Building2 className="text-primary-500" size={20} />
                        <h3 className="font-semibold text-gray-900">{group.hotel}</h3>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-gray-600 text-sm">
                        <Calendar size={16} />
                        <span>{group.period}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-red-600 font-medium">
                        <Wallet size={16} />
                        <span>
                          {group.totalAmount} SAR for {group.totalRequests} {group.totalRequests === 1 ? 'upgrade' : 'upgrades'}
                        </span>
                      </div>
                    </div>
                    
                    {selectedGroup !== group.key ? (
                      <button 
                        onClick={() => setSelectedGroup(group.key)}
                        className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                      >
                        Review Deduction
                      </button>
                    ) : (
                      <button 
                        onClick={() => setSelectedGroup(null)}
                        className="px-4 py-2 bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300 transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                  
                  {selectedGroup === group.key && (
                    <div className="p-4 border-t">
                      <div className="space-y-4">
                        {/* Request List */}
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <h4 className="font-medium mb-2">Upgrade Details</h4>
                          <div className="max-h-60 overflow-y-auto">
                            <table className="w-full">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Pilgrim</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Upgrade</th>
                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Price</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {group.requests.map(request => (
                                  <tr key={request.id} className="hover:bg-gray-100">
                                    <td className="px-3 py-2 whitespace-nowrap">
                                      <div className="text-sm font-medium text-gray-900">{request.pilgrimName}</div>
                                      <div className="text-xs text-gray-500">{request.passportNumber}</div>
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap">
                                      <div className="text-xs font-medium">
                                        {request.fromRoomType} → {request.toRoomType}
                                      </div>
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-right">
                                      <div className="text-sm font-medium text-red-600">{request.price} SAR</div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="bg-gray-100">
                                  <td colSpan={2} className="px-3 py-2 text-right font-medium">Total:</td>
                                  <td className="px-3 py-2 text-right font-bold text-red-600">{group.totalAmount} SAR</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                        
                        {/* Balance Deduction Warning */}
                        <div className="bg-red-50 p-4 rounded-lg">
                          <p className="text-red-700 font-medium">
                            This will deduct {group.totalAmount} SAR from your organization's balance.
                          </p>
                          <p className="text-red-600 text-sm mt-1">
                            The deduction is final and cannot be reversed once confirmed.
                          </p>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex justify-between pt-2">
                          <button
                            onClick={() => handleRejectGroup(group)}
                            disabled={isProcessing}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                          >
                            <Trash2 size={18} />
                            <span>Reject All</span>
                          </button>
                          
                          <button
                            onClick={() => handleGroupApproval(group)}
                            disabled={isProcessing}
                            className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                          >
                            {isProcessing ? (
                              <>
                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                                <span>Processing...</span>
                              </>
                            ) : (
                              <>
                                <Wallet size={18} />
                                <span>Confirm Balance Deduction</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </>
        )}
        
        {/* Bottom Actions */}
        <div className="flex justify-end pt-4 border-t">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {translations?.common?.close || "Close"}
          </button>
        </div>
      </div>
    </Modal>
  );
};
